/**
 * The "Powered by KirokuForms" link, tagged with where the click came from.
 *
 * Every surface that renders the attribution builds its href here. It used to be
 * `?ref=form` everywhere, and VeritaMetrics reads `ref` as the source
 * (`veritametrics/src/utils/tracking/dataEnrichment.ts`, `extractUtms`), so
 * every click from every customer's form landed in one row called "form". The
 * source is now the site the form sits on, which is the number worth having:
 * which customers' forms send us visitors.
 *
 * Only a hostname ever goes into the link, never a path or query: a referring
 * URL can carry an email address or a session token, a hostname cannot.
 *
 * Pure and dependency-free, because `src/scripts/kiroku-embed.ts` bundles it
 * into the embed script that runs on customers' sites.
 */

/** Which renderer drew the link. Goes in `utm_content`. */
export type AttributionSurface =
  | "embed"
  | "hosted"
  | "html_snippet"
  | "preview";

export const ATTRIBUTION_MEDIUM = "powered_by";
export const ATTRIBUTION_CAMPAIGN = "form_attribution";

// A DNS hostname: labels of letters, digits and hyphens, dot-separated.
const HOSTNAME_RE =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

/**
 * A hostname fit to put in a link, or null. Accepts a bare hostname or a full
 * URL (a Referer header), lowercases it, and drops a leading "www." so
 * `www.example.com` and `example.com` report as one source.
 */
export function attributionSource(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  let host = raw.trim().toLowerCase();
  if (host.includes("/")) {
    try {
      host = new URL(host).hostname;
    } catch {
      return null;
    }
  }
  host = host.replace(/^www\./, "");
  return HOSTNAME_RE.test(host) ? host : null;
}

/**
 * The attribution href. `source` is the site the visitor clicked from; when it
 * cannot be known (a static HTML snippet has no script) it is left out, and the
 * browser's Referer on the click still tells analytics the origin.
 */
export function attributionHref(
  base: string,
  surface: AttributionSurface,
  source?: string | null,
): string {
  const params = new URLSearchParams();
  const host = attributionSource(source);
  if (host) params.set("utm_source", host);
  params.set("utm_medium", ATTRIBUTION_MEDIUM);
  params.set("utm_campaign", ATTRIBUTION_CAMPAIGN);
  params.set("utm_content", surface);
  return `${base.replace(/\/+$/, "")}/?${params.toString()}`;
}
